import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'

import { API } from '../../../api/apis.config'
import { createPeriod } from '../../../api/periods/create-period'
import { updatePeriod } from '../../../api/periods/edit-period'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import PeriodForm from '../../../components/pages/period/period-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { PeriodTypes } from '../../../lib/types/models/period/period'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const PeriodsPage = () => {
  const tableRef = React.useRef<DynamicTableRef<PeriodTypes.Full>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    PeriodTypes.Full
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updatePeriod,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
    }
  })

  const onSubmitEdit = (values: PeriodTypes.Input) => {
    if (!selectedData) return
    mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createPeriod,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
    }
  })

  const columns: Column<PeriodTypes.Full>[] = [
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => data.row.original.name
    },
    {
      accessorKey: 'startDate',
      header: 'Date de début',
      enableSorting: true,
      cell: (data) => DateFormatter.DDMMYYYY(data.row.original.startDate)
    },
    {
      accessorKey: 'endDate',
      header: 'Date de fin',
      enableSorting: true,
      cell: (data) => DateFormatter.DDMMYYYY(data.row.original.endDate)
    },
    {
      accessorKey: 'displayName',
      header: 'NOM affiché',
      enableSorting: true,
      cell: (data) => (!data.row.original.displayName ? '-' : data.row.original.displayName)
    },
    {
      accessorKey: 'color',
      header: 'Couleur',
      cell: (data) =>
        data.row.original.color ? (
          <div
            className="rounded-full w-5 h-5"
            style={{
              backgroundColor: data.row.original.color
            }}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Périodes" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<PeriodTypes.Full>
        ref={tableRef}
        title="Périodes"
        version="FULL"
        deleteMessage={{
          name: 'périodes',
          attributeName: 'name',
          deleteOne: (period) => `Êtes-vous sûr de vouloir supprimer la période "${period.name}" ?`
        }}
        sorting={{ id: 'startDate', desc: false }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.periods.getAll.url}
        actions={[
          { type: 'global', icon: PlusIcon, label: 'Créer', fn: () => toggleModal('create') }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <PeriodForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <PeriodForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default PeriodsPage
